import {
  AlertCircle,
  ExternalLink,
  FolderOpen,
  Loader2,
  RefreshCw,
  Save,
  Server,
  Square,
  Upload,
} from "lucide-react";
import { useT } from "@magic-md/i18n/react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import {
  getFolderPublishConfigPath,
  getFolderPublishDir,
  parseFolderPublishConfigFile,
  resolveFolderPublishOutDir,
} from "../../lib/publish-utils";
import { getPlatformService } from "../../platform";
import { useEditorStore } from "../../store/editor-store";
import type { SitePublishTaskState } from "../../store/publish-store";
import { usePublishStore } from "../../store/publish-store";
import { useShallow } from "zustand/shallow";
import { showFolderPickerDialog } from "./DialogHost";
import { DialogButton, DialogOverlay } from "./DialogOverlay";
import { PublishTargetSummary } from "./PublishTargetSummary";

// ── Module-level listener pattern ──

interface FolderPublishDialogRequest {
  folderPath: string;
  resolve: () => void;
}

let _listener: ((req: FolderPublishDialogRequest) => void) | null = null;

export function showFolderPublishDialog(folderPath: string): Promise<void> {
  return new Promise((resolve) => {
    if (_listener) {
      _listener({ folderPath, resolve });
    } else {
      console.warn("[folder-publish-dialog] No host mounted");
      resolve();
    }
  });
}

export function FolderPublishDialogHost(): ReactNode {
  const [request, setRequest] =
    useState<FolderPublishDialogRequest | null>(null);

  useEffect(() => {
    _listener = (req) => setRequest(req);
    return () => {
      _listener = null;
    };
  }, []);

  if (!request) return null;
  return (
    <FolderPublishDialog
      folderPath={request.folderPath}
      onClose={() => {
        request.resolve();
        setRequest(null);
      }}
    />
  );
}

// ── Default values ──

interface FolderPublishDialogValues {
  outDir: string;
  clean: boolean;
  rerunAll: boolean;
  incremental: boolean;
  siteTitle: string;
}

function buildDefaultValues(folderPath: string): FolderPublishDialogValues {
  return {
    outDir: resolveFolderPublishOutDir(folderPath),
    clean: false,
    rerunAll: false,
    incremental: true,
    siteTitle: folderPath.split("/").pop() || folderPath,
  };
}

// ── Phase type ──

type DialogPhase = "config" | "running" | "completed" | "failed";

// ── Preview Server Section ──

function PreviewServerSection({ outputDir }: { outputDir?: string }) {
  const t = useT("editor");
  const siteServer = usePublishStore((s) => s.siteServer);
  const startSiteServer = usePublishStore((s) => s.startSiteServer);
  const stopSiteServer = usePublishStore((s) => s.stopSiteServer);

  if (!outputDir) return null;

  return (
    <section className="rounded-lg border border-border/70 bg-muted/20 p-3">
      <div className="flex items-center gap-2 text-xs font-medium mb-2">
        <Server size={13} />
        {t("publish.previewServer")}
      </div>
      {siteServer?.running ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[11px] text-emerald-600">{t("publish.serverRunning")}</span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={siteServer.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                e.preventDefault();
                getPlatformService().openExternal?.(siteServer.url);
              }}
              className="inline-flex items-center gap-1 font-mono text-[11px] text-primary hover:underline"
            >
              {siteServer.url}
              <ExternalLink size={11} />
            </a>
          </div>
          <DialogButton
            onClick={() => {
              void stopSiteServer();
            }}
          >
            <span className="inline-flex items-center gap-1">
              <Square size={12} />
              {t("publish.action.stopServer")}
            </span>
          </DialogButton>
        </div>
      ) : siteServer?.starting ? (
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <Loader2 size={12} className="animate-spin" />
          {t("publish.serverStarting")}
        </div>
      ) : (
        <DialogButton
          onClick={() => {
            void startSiteServer(outputDir);
          }}
        >
          <span className="inline-flex items-center gap-1">
            <Server size={12} />
            {t("publish.action.startPreviewServer")}
          </span>
        </DialogButton>
      )}
    </section>
  );
}

// ── Main Dialog Component ──

function FolderPublishDialog({
  folderPath,
  onClose,
}: {
  folderPath: string;
  onClose: () => void;
}) {
  const t = useT("editor");
  const setStatusMessage = useEditorStore((s) => s.setStatusMessage);
  const folderTasks = usePublishStore(useShallow((s) => s.folderTasks));
  const { publishFolder, saveFolderPublishConfig, cancelFolderPublish, clearFolderTask } =
    usePublishStore(
      useShallow((s) => ({
        publishFolder: s.publishFolder,
        saveFolderPublishConfig: s.saveFolderPublishConfig,
        cancelFolderPublish: s.cancelFolderPublish,
        clearFolderTask: s.clearFolderTask,
      })),
    );

  const folderTask: SitePublishTaskState | undefined = folderTasks[folderPath];
  const defaultOutDir = useMemo(
    () => resolveFolderPublishOutDir(folderPath),
    [folderPath],
  );
  const publishPackagePath = useMemo(
    () => getFolderPublishDir(folderPath),
    [folderPath],
  );

  const [values, setValues] = useState<FolderPublishDialogValues>(() =>
    buildDefaultValues(folderPath),
  );
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<"save" | "publish" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  // Determine current phase
  const phase: DialogPhase = useMemo(() => {
    if (!folderTask) return "config";
    if (folderTask.status === "running") return "running";
    if (folderTask.status === "completed") return "completed";
    if (folderTask.status === "failed") return "failed";
    return "config";
  }, [folderTask]);

  // Load saved config on mount
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setWarning(null);

    const defaults = buildDefaultValues(folderPath);
    const configPath = getFolderPublishConfigPath(folderPath);

    void (async () => {
      try {
        const engine = getPlatformService().engine;
        const { exists } = await engine.workspaceExists({ path: configPath });
        if (!exists) {
          if (!cancelled) setValues(defaults);
          return;
        }

        const { content } = await engine.workspaceReadFile({
          path: configPath,
        });
        const parsed = parseFolderPublishConfigFile(content, folderPath);
        if (!cancelled) {
          if (parsed) {
            setValues({
              outDir: parsed.settings.outDir || defaultOutDir,
              clean: parsed.settings.clean,
              rerunAll: parsed.settings.rerunAll,
              incremental: parsed.settings.incremental,
              siteTitle: parsed.settings.siteTitle || defaults.siteTitle,
            });
          } else {
            setValues(defaults);
            setWarning(t("publish.warning.invalidConfig"));
          }
        }
      } catch (err) {
        if (!cancelled) {
          setValues(defaults);
          setWarning(
            t("publish.warning.readConfigFailed", {
              message: err instanceof Error ? err.message : String(err),
            }),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [defaultOutDir, folderPath]);

  const resolvedOutDir = values.outDir.trim() || defaultOutDir;
  const buildStrategyLabel = values.incremental
    ? t("publish.buildStrategy.incrementalHash")
    : t("publish.buildStrategy.full");

  // ── Handlers ──

  const handleSave = async () => {
    if (!resolvedOutDir) {
      setError(t("publish.error.outDirRequired"));
      return;
    }
    setBusy("save");
    setError(null);
    try {
      await saveFolderPublishConfig(folderPath, {
        outDir: resolvedOutDir,
        clean: values.clean,
        rerunAll: values.rerunAll,
        incremental: values.incremental,
        siteTitle: values.siteTitle,
      });
      setStatusMessage(t("publish.status.configSaved"));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const handlePublish = async () => {
    if (!resolvedOutDir) {
      setError(t("publish.error.outDirRequired"));
      return;
    }
    setBusy("publish");
    setError(null);
    try {
      await publishFolder(folderPath, {
        outDir: resolvedOutDir,
        clean: values.clean,
        rerunAll: values.rerunAll,
        incremental: values.incremental,
        siteTitle: values.siteTitle,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const handleCancel = async () => {
    try {
      await cancelFolderPublish(folderPath);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleRetry = () => {
    clearFolderTask(folderPath);
    setError(null);
  };

  const handleClose = () => {
    onClose();
  };

  const handleRedo = () => {
    clearFolderTask(folderPath);
    setError(null);
  };

  // ── Title ──

  const title =
    phase === "running"
      ? t("publish.folderTitle.running")
      : phase === "completed"
        ? t("publish.folderTitle.completed")
        : phase === "failed"
          ? t("publish.folderTitle.failed")
          : t("publish.folder");

  return (
    <DialogOverlay
      open
      onClose={busy ? () => {} : onClose}
      title={title}
      className="w-[680px]"
      footer={
        phase === "config" ? (
          <>
            <DialogButton onClick={handleClose} disabled={!!busy}>
              {t("common.cancel")}
            </DialogButton>
            <DialogButton
              onClick={() => {
                void handleSave();
              }}
              disabled={loading || !!busy}
            >
              <span className="inline-flex items-center gap-1">
                <Save size={12} />
                {t("publish.action.saveConfig")}
              </span>
            </DialogButton>
            <DialogButton
              variant="primary"
              onClick={() => {
                void handlePublish();
              }}
              disabled={loading || !!busy}
            >
              <span className="inline-flex items-center gap-1">
                {busy === "publish" ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Upload size={12} />
                )}
                {t("publish.action.start")}
              </span>
            </DialogButton>
          </>
        ) : phase === "running" ? (
          <>
            <DialogButton onClick={handleClose} disabled>
              {t("common.close")}
            </DialogButton>
            <DialogButton variant="primary" onClick={() => void handleCancel()}>
              <span className="inline-flex items-center gap-1">
                <Square size={12} />
                {t("publish.action.cancelPublish")}
              </span>
            </DialogButton>
          </>
        ) : phase === "completed" ? (
          <>
            <DialogButton onClick={handleClose}>{t("common.close")}</DialogButton>
            <DialogButton variant="primary" onClick={handleRedo}>
              <span className="inline-flex items-center gap-1">
                <RefreshCw size={12} />
                {t("publish.action.republish")}
              </span>
            </DialogButton>
          </>
        ) : (
          // failed
          <>
            <DialogButton onClick={handleClose}>{t("common.close")}</DialogButton>
            <DialogButton variant="primary" onClick={handleRetry}>
              <span className="inline-flex items-center gap-1">
                <RefreshCw size={12} />
                {t("publish.action.retry")}
              </span>
            </DialogButton>
          </>
        )
      }
    >
      <div className="flex h-full flex-col overflow-auto px-4 py-4">
        <div className="space-y-4">
          <PublishTargetSummary
            kind="folder"
            targetPath={folderPath}
            publishPackagePath={publishPackagePath}
            details={[
              {
                label: t("publish.field.outputDir"),
                value: resolvedOutDir || t("publish.notSet"),
                title: resolvedOutDir,
                mono: true,
              },
              {
                label: t("publish.field.siteTitle"),
                value: values.siteTitle || t("publish.untitledSite"),
                title: values.siteTitle,
              },
              {
                label: t("publish.field.buildStrategy"),
                value: buildStrategyLabel,
                title: buildStrategyLabel,
              },
            ]}
          />

          {/* Phase: Config */}
          {phase === "config" && (
            <section className="space-y-3">
              <div className="space-y-1">
                <label className="text-[11px] font-medium text-muted-foreground">
                  {t("publish.field.siteTitle")}
                </label>
                <input
                  type="text"
                  value={values.siteTitle}
                  onChange={(event) =>
                    setValues((prev) => ({
                      ...prev,
                      siteTitle: event.target.value,
                    }))
                  }
                  disabled={loading || !!busy}
                  className="h-8 w-full rounded border border-border bg-background px-2 font-mono text-[11px]"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-medium text-muted-foreground">
                  {t("publish.field.outputDir")}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={values.outDir}
                    onChange={(event) =>
                      setValues((prev) => ({
                        ...prev,
                        outDir: event.target.value,
                      }))
                    }
                    disabled={loading || !!busy}
                    placeholder={defaultOutDir}
                    className="h-8 flex-1 rounded border border-border bg-background px-2 font-mono text-[11px]"
                  />
                  <button
                    type="button"
                    disabled={loading || !!busy}
                    onClick={() => {
                      void (async () => {
                        const chosen = await showFolderPickerDialog({
                          title: t("publish.chooseFolderOutputDir"),
                        });
                        if (chosen) {
                          setValues((prev) => ({
                            ...prev,
                            outDir: chosen,
                          }));
                        }
                      })();
                    }}
                    className="inline-flex h-8 items-center justify-center rounded border border-border bg-muted px-2 text-muted-foreground transition-colors hover:bg-muted/80 disabled:cursor-not-allowed disabled:opacity-50"
                    title={t("dialog.selectFolder")}
                  >
                    <FolderOpen size={13} />
                  </button>
                </div>
                <p className="text-[10px] text-muted-foreground/70">
                  {t("publish.defaultOutDirHelp", { dir: defaultOutDir })}
                </p>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-[11px]">
                  <input
                    type="checkbox"
                    checked={values.clean}
                    disabled={loading || !!busy}
                    onChange={(event) =>
                      setValues((prev) => ({
                        ...prev,
                        clean: event.target.checked,
                      }))
                    }
                  />
                  {t("publish.option.clean")}
                </label>
                <label className="flex items-center gap-2 text-[11px]">
                  <input
                    type="checkbox"
                    checked={values.rerunAll}
                    disabled={loading || !!busy}
                    onChange={(event) =>
                      setValues((prev) => ({
                        ...prev,
                        rerunAll: event.target.checked,
                      }))
                    }
                  />
                  {t("publish.option.rerunAll")}
                </label>
                <label className="flex items-center gap-2 text-[11px]">
                  <input
                    type="checkbox"
                    checked={values.incremental}
                    disabled={loading || !!busy}
                    onChange={(event) =>
                      setValues((prev) => ({
                        ...prev,
                        incremental: event.target.checked,
                      }))
                    }
                  />
                  {t("publish.option.incrementalHash")}
                </label>
              </div>

              {(warning || error) && (
                <section className="space-y-2">
                  {warning && (
                    <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-700">
                      {warning}
                    </div>
                  )}
                  {error && (
                    <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700">
                      <span className="inline-flex items-center gap-1">
                        <AlertCircle size={12} />
                        {error}
                      </span>
                    </div>
                  )}
                </section>
              )}
            </section>
          )}

          {/* Phase: Running */}
          {phase === "running" && folderTask && (
            <RunningPhase task={folderTask} />
          )}

          {/* Phase: Completed */}
          {phase === "completed" && folderTask?.result && (
            <CompletedPhase
              result={folderTask.result}
              outputDir={folderTask.result.outputDir}
            />
          )}

          {/* Phase: Failed */}
          {phase === "failed" && folderTask && (
            <section className="rounded border border-red-200 bg-red-50 px-3 py-3 text-[11px] text-red-700">
              <div className="flex items-start gap-2">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">{t("publish.failed")}</p>
                  <p className="mt-1 text-red-600">
                    {folderTask.error || t("publish.unknownError")}
                  </p>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </DialogOverlay>
  );
}

// ── Running Phase Sub-component ──

function RunningPhase({ task }: { task: SitePublishTaskState }) {
  const t = useT("editor");
  return (
    <section className="space-y-3">
      <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
        <div className="flex items-center gap-2 mb-2">
          <Loader2 size={14} className="animate-spin text-primary" />
          <span className="text-xs font-medium">
            {task.progress?.message ?? t("publish.running")}
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-muted rounded-full h-1.5 mb-2">
          <div
            className="bg-primary h-1.5 rounded-full transition-all duration-300"
            style={{
              width: `${Math.min(task.progress?.percent ?? 0, 100)}%`,
            }}
          />
        </div>

        {/* Page count */}
        {task.pagesTotal != null && task.pagesTotal > 0 && (
          <div className="space-y-1">
            <div className="text-[11px] text-muted-foreground">
              {t("publish.processingProgress", {
                processed: task.pagesProcessed ?? 0,
                total: task.pagesTotal,
              })}
            </div>
            {task.currentPage && (
              <div
                className="text-[10px] text-muted-foreground/60 font-mono truncate"
                title={task.currentPage}
              >
                → {task.currentPage}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// ── Completed Phase Sub-component ──

function CompletedPhase({
  result,
  outputDir,
}: {
  result: NonNullable<SitePublishTaskState["result"]>;
  outputDir: string;
}) {
  const t = useT("editor");
  return (
    <section className="space-y-3">
      {/* Stats cards */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-center">
          <div className="text-lg font-bold text-emerald-600">
            {result.builtPages ?? 0}
          </div>
          <div className="text-[10px] text-emerald-600/70">{t("publish.stat.built")}</div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-center">
          <div className="text-lg font-bold text-amber-600">
            {result.cachedPages ?? 0}
          </div>
          <div className="text-[10px] text-amber-600/70">{t("publish.stat.cached")}</div>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-center">
          <div className="text-lg font-bold text-red-600">
            {result.failedPages ?? 0}
          </div>
          <div className="text-[10px] text-red-600/70">{t("publish.stat.failed")}</div>
        </div>
      </div>

      {/* Output info */}
      <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
        <div className="space-y-1">
          <div className="text-[11px] text-muted-foreground">
            {t("publish.totalPages", { total: result.totalPages ?? 0 })}
          </div>
          <div className="text-[10px] text-muted-foreground/60 font-mono">
            {t("publish.outputPath", { outputDir })}
          </div>
          <div className="text-[10px] text-muted-foreground/60">
            {t("publish.generatedAt", { generatedAt: result.generatedAt })}
          </div>
        </div>
      </div>

      {/* Preview server */}
      <PreviewServerSection outputDir={outputDir} />
    </section>
  );
}
